/**
 * Chasis de Factura CXP — migración web de ares8550.
 *
 * Busca una factura de cuentas por pagar por su número y permite, por cada
 * chasis, modificar el costo (valor de la línea en la factura), el precio de
 * venta y el precio mínimo, o eliminar el chasis de la factura Y DEL
 * INVENTARIO físicamente (fiel al escritorio).
 *
 * A diferencia del escritorio, las validaciones se aplican unificadas en el
 * backend (préstamo activo, facturado a cliente, incauto activo, movimientos
 * del producto) — ares8550 las aplicaba a medias según la ruta de entrada.
 * También se valida que el nuevo total de la factura no quede por debajo de
 * lo ya pagado.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  PageHeader,
  fpInputSx,
  fpDataGridSx,
  RazonCancelacionTexto,
} from "components/financepro";
import { useApi } from "lib/api";
import { imprimirDevolucionCxp } from "modules/cuentasxpagar/impresionfacturacxp";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Busque una factura por su número
      </Typography>
    </Stack>
  );
}

export default function ChasisFacturaCxp() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [nfactura, setNfactura] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [factura, setFactura] = useState(null);
  const [lineas, setLineas] = useState([]);

  const bloqueada = !!factura?.anulada;

  const buscar = async (numero = null) => {
    const nf = numero || nfactura.trim();
    if (!nf) {
      showAlert({ message: "Debe indicar el número de la factura", color: "warning" });
      return;
    }
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/cxp_factura_detalle", { nfactura: nf });
      if (ok) {
        setFactura(data.factura || null);
        setLineas(
          (data.lineas || []).map((l) => ({
            ...l,
            id: l.chasis,
            costo: String(toNum(l.valor)),
            precioventa: String(toNum(l.precio)),
            preciominimo: String(toNum(l.mprecio)),
          }))
        );
      } else {
        setFactura(null);
        setLineas([]);
        showAlert({ message: data.Error || "No se pudo cargar la factura", color: "error" });
      }
    } finally {
      setBuscando(false);
    }
  };

  const setCampo = (chasis, campo, valor) => {
    setLineas((prev) => prev.map((l) => (l.chasis === chasis ? { ...l, [campo]: valor } : l)));
  };

  const avisarTotal = (data, prefijo) => {
    showAlert({
      message: `${prefijo}. Nuevo total de la factura RD$ ${fmtMoney(data.total)}`,
      color: "success",
    });
  };

  // ---- Guardar precio de un chasis ----
  const [guardando, setGuardando] = useState("");

  const guardarPrecio = async (row) => {
    if (toNum(row.costo) < 0 || toNum(row.precioventa) < 0 || toNum(row.preciominimo) < 0) {
      showAlert({ message: "Los valores no pueden ser negativos", color: "warning" });
      return;
    }
    setGuardando(row.chasis);
    try {
      const { ok, data } = await post("/api/cxp_chasis_precio", {
        id: factura.id,
        chasis: row.chasis,
        valor: toNum(row.costo),
        precio: toNum(row.precioventa),
        mprecio: toNum(row.preciominimo),
      });
      if (ok) {
        avisarTotal(data, data.ok || "Precios guardados");
        buscar(factura.nfactura);
      } else {
        showAlert({ message: data.Error || "No se pudieron guardar los precios", color: "error" });
      }
    } finally {
      setGuardando("");
    }
  };

  // ---- Devolver chasis al suplidor (elimina de factura + inventario) ----
  const [target, setTarget] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [eliminando, setEliminando] = useState(false);

  const eliminarChasis = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/cxp_chasis_eliminar", {
        id: factura.id,
        chasis: target.chasis,
        motivo: motivo.trim(),
      });
      if (ok) {
        setTarget(null);
        setMotivo("");
        avisarTotal(data, data.ok || "Devolución registrada");
        // QA: la devolución queda registrada y se imprime su comprobante.
        if (data.devolucion) imprimirDevolucionCxp(data.devolucion);
        buscar(factura.nfactura);
      } else {
        showAlert({ message: data.Error || "No se pudo devolver el chasis", color: "error" });
      }
    } finally {
      setEliminando(false);
    }
  };

  const columnas = [
    { field: "chasis", headerName: "Chasis", width: 180 },
    { field: "marca", headerName: "Marca", width: 110 },
    { field: "modelo", headerName: "Modelo", width: 110 },
    { field: "colore", headerName: "Color", width: 100 },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.estado ? (
          <Chip
            size="small"
            label={params.row.estado}
            sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Disponible"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "costo",
      headerName: "Costo",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.costo}
          onChange={(e) => setCampo(params.row.chasis, "costo", e.target.value)}
          disabled={bloqueada}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "precioventa",
      headerName: "Precio venta",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.precioventa}
          onChange={(e) => setCampo(params.row.chasis, "precioventa", e.target.value)}
          disabled={bloqueada}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "preciominimo",
      headerName: "Precio mínimo",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.preciominimo}
          onChange={(e) => setCampo(params.row.chasis, "preciominimo", e.target.value)}
          disabled={bloqueada}
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 290,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={
              guardando === params.row.chasis ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={() => guardarPrecio(params.row)}
            disabled={bloqueada || guardando === params.row.chasis}
            sx={btnOutline(FP.color.navy)}
          >
            Guardar precio
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={bloqueada}
            sx={btnOutline(FP.color.danger)}
          >
            Eliminar
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Chasis de Factura CXP"
        subtitle="Modifica costo y precios de los chasis de una factura de suplidor, o elimínalos del inventario"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Búsqueda */}
        <Box sx={sectionBox}>
          <SectionTitle icon={SearchIcon} color={FP.color.accentBlue}>
            Factura
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="# Factura"
                value={nfactura}
                onChange={(e) => setNfactura(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscar()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  buscando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={() => buscar()}
                disabled={buscando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Buscar
              </Button>
            </Grid>
            <Grid item xs={12} md={7} sx={{ display: "flex", alignItems: "center" }}>
              {factura && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={factura.suplidornombre}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Fecha ${factura.fecha}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Valor RD$ ${fmtMoney(factura.valor)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                  <Chip
                    label={`Pagado RD$ ${fmtMoney(factura.vplicado)}`}
                    sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
                  />
                  {factura.anulada ? (
                    <Chip
                      label="Anulada"
                      sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
                    />
                  ) : (
                    <Chip
                      label={factura.status === "C" ? "Saldada" : "Vigente"}
                      sx={
                        factura.status === "C"
                          ? { bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }
                          : { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }
                      }
                    />
                  )}
                </Stack>
              )}
            </Grid>
          </Grid>
          {bloqueada && (
            <Box
              sx={{
                mt: 2,
                p: 1.6,
                borderRadius: 1.8,
                bgcolor: FP.color.warningSoft,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ color: FP.color.warning, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#92400E" }}>
                La factura está anulada — no se pueden modificar ni eliminar sus chasis.
              </Typography>
              <RazonCancelacionTexto razon={factura.razonanula} sx={{ ml: 1 }} />
            </Box>
          )}
        </Box>

        {/* Líneas / chasis */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={DirectionsCarOutlinedIcon} color={FP.color.success}>
            Chasis de la factura
          </SectionTitle>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={lineas}
              columns={columnas}
              loading={buscando}
              rowHeight={52}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
          {factura && (
            <Typography sx={{ mt: 2, fontWeight: 800, color: FP.color.textPrimary }}>
              Total factura: RD$ {fmtMoney(factura.valor)} — Balance: RD${" "}
              {fmtMoney(factura.balance)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Confirmación de eliminar chasis */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Devolver chasis al suplidor
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Chasis <b>{target?.chasis}</b> (
            {[target?.marca, target?.modelo].filter(Boolean).join(" ")}) de la factura{" "}
            <b>{factura?.nfactura}</b>. Se registrará la devolución al suplidor y el chasis se
            retirará de la factura y del inventario. Al confirmar se imprime el comprobante de
            devolución. ¿Desea continuar?
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Motivo de la devolución (opcional)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 250 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminarChasis}
            disabled={eliminando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, devolver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
